import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { usersApi } from '../api/users.api';
import { getErrorMessage } from '@/core/api/response-handler';

/**
 * Users Mutations Hook
 *
 * Centralized mutations for user operations.
 * Handles optimistic updates, cache invalidation, and error handling.
 *
 * Usage:
 * ```tsx
 * const mutations = useUsersMutations();
 * await mutations.updatePhoneNote.mutateAsync({ userId: '123', phoneCallNote: 'Called on...' });
 * ```
 */
export function useUsersMutations() {
    const queryClient = useQueryClient();

    /**
     * Update phone call note for a user
     */
    const updatePhoneNote = useMutation({
        mutationFn: ({ userId, phoneCallNote }: { userId: string; phoneCallNote: string }) =>
            usersApi.updateActivation({
                id: userId,
                phoneCallNote: phoneCallNote.trim()
            }),
        onMutate: () => {
            const toastId = toast.loading('Mesajul se salveaza...', { autoClose: false });
            return { toastId };
        },
        onSuccess: (_, __, context) => {
            if (context?.toastId) toast.dismiss(context.toastId);
            toast.success('Mesajul a fost salvat', { autoClose: 3000 });
            queryClient.invalidateQueries({ queryKey: ['users', 'report'] });
        },
        onError: (error, _, context) => {
            if (context?.toastId) toast.dismiss(context.toastId);
            const errorMessage = getErrorMessage(error, 'Mesajul nu s-a putut salva');
            toast.error(errorMessage, { autoClose: 5000 });
        },
    });

    return {
        updatePhoneNote,
        isUpdatingPhoneNote: updatePhoneNote.isPending,
        isLoading: updatePhoneNote.isPending,
    };
}
